import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EuDto } from '@nexital/shared';
import { ProvedorDependencias, type Dependencias } from '../application/dependencias';
import { criarStoreColaboracao } from '../application/colaboracao-store';
import { criarStoreNotas } from '../application/notas-store';
import { criarStoreEnvio } from '../application/envio-store';
import { criarApiConfiguracoes } from '../infrastructure/api-configuracoes';
import { criarApiMembros } from '../infrastructure/api-membros';
import { criarStoreNotificacoes } from '../application/notificacoes-store';
import { criarStoreQuadro } from '../application/quadro-store';
import { GerenciadorSessao } from '../application/sessao';
import { caminhoDoEspaco, escolherEspaco, lerEspaco, type Espaco } from '../domain/espaco';
import { ApiAcessoHttp } from '../infrastructure/api-acesso';
import { prepararRecaptcha } from '../infrastructure/recaptcha';
import { ApiHttp } from '../infrastructure/api-http';
import { CanalSocket } from '../infrastructure/canal-socket';
import { ApiNotasHttp } from '../infrastructure/api-notas';
import { ArmazenamentoNavegador } from '../infrastructure/armazenamento-local';
import { CanalAbasNavegador } from '../infrastructure/canal-abas';
import { TelaAviso } from '../presentation/acesso/TelaAviso';
import { TelaConvite } from '../presentation/acesso/TelaConvite';
import { TelaEntrar } from '../presentation/acesso/TelaEntrar';
import { TelaSolicitarAcesso } from '../presentation/acesso/TelaSolicitarAcesso';
import { TelaQuadro } from '../presentation/quadro/TelaQuadro';
import { AvisoInatividade } from '../presentation/shell/AvisoInatividade';
import { limiteDaEmpresa } from '../domain/inatividade';

// Composição do front: um único conjunto de adaptadores por aba.
const sessao = new GerenciadorSessao(new ApiAcessoHttp(), new CanalAbasNavegador());
// Id desta aba: vai nas gravações e no tempo real, para a aba não reagir ao que ela mesma gravou.
const clienteId = crypto.randomUUID();
const api = new ApiHttp(sessao, clienteId);
const canal = new CanalSocket(sessao, clienteId);
const apiNotas = new ApiNotasHttp((metodo, caminho, corpo) => api.chamar(metodo, caminho, corpo));
const storeNotificacoes = criarStoreNotificacoes(api);
const armazenamento = new ArmazenamentoNavegador();
// Saída por inatividade (RQ-ACE-014): a tela de login explica por que a pessoa saiu.
const sairPorInatividade = () => {
  window.history.replaceState(null, '', `${window.location.pathname}?erro=inatividade`);
  void sessao.sair();
};

type Estado =
  | { fase: 'carregando' }
  | { fase: 'entrar' }
  | { fase: 'falha' }
  | { fase: 'conectado'; eu: EuDto; espaco: Espaco | 'sem-acesso' | null };

const ERROS_ENTRADA: Record<string, string> = {
  google: 'Não foi possível entrar com o Google. Use uma conta já cadastrada no Nexital.',
  'google-indisponivel': 'A entrada com o Google não está disponível neste ambiente. Entre com e-mail e senha.',
  inatividade: 'Você saiu por inatividade. Entre de novo para continuar.',
};

/** Espaço pedido pela URL (ou o primeiro disponível), já refletido na barra de endereço. */
function espacoDaUrl(eu: EuDto, empilhar = false) {
  const espaco = escolherEspaco(eu.empresas, lerEspaco(window.location.pathname));
  if (espaco && espaco !== 'sem-acesso') {
    const caminho = caminhoDoEspaco(espaco);
    if (caminho !== window.location.pathname) window.history[empilhar ? 'pushState' : 'replaceState'](null, '', caminho);
  }
  return espaco;
}

function useSessao() {
  const [estado, definirEstado] = useState<Estado>({ fase: 'carregando' });

  const carregarEu = useCallback(async () => {
    try {
      const eu = await api.obterEu();
      definirEstado({ fase: 'conectado', eu, espaco: espacoDaUrl(eu) });
    } catch {
      definirEstado({ fase: sessao.token ? 'falha' : 'entrar' });
    }
  }, []);

  useEffect(() => {
    const cancelar = sessao.assinar((autenticado) => (autenticado ? void carregarEu() : definirEstado({ fase: 'entrar' })));
    void sessao.iniciar().then((conectado) => conectado || definirEstado({ fase: 'entrar' }));
    return cancelar;
  }, [carregarEu]);

  // Voltar e avançar do navegador trocam de quadro sem recarregar.
  useEffect(() => {
    const aoNavegar = () => definirEstado((s) => (s.fase === 'conectado' ? { ...s, espaco: espacoDaUrl(s.eu) } : s));
    window.addEventListener('popstate', aoNavegar);
    return () => window.removeEventListener('popstate', aoNavegar);
  }, []);

  const navegar = useCallback((espaco: Espaco) => {
    window.history.pushState(null, '', caminhoDoEspaco(espaco));
    definirEstado((s) => (s.fase === 'conectado' ? { ...s, espaco } : s));
  }, []);

  return { estado, navegar, recarregar: carregarEu };
}

/** Recria o store do quadro a cada troca de espaço (RQ-ACE-011). */
type PropsEspaco = { eu: EuDto; espaco: Espaco; navegar: (e: Espaco) => void; recarregar: () => Promise<void> };

function EspacoTrabalho({ eu, espaco, navegar, recarregar }: PropsEspaco) {
  // O store do quadro vive enquanto o quadro for o mesmo: abrir e fechar a licitação não o recria (RQ-QDR-008).
  const storeQuadro = useMemo(() => criarStoreQuadro(api, espaco.quadroUuid), [espaco.quadroUuid]);
  // Presença, travas e arrastes são do quadro: começam vazios a cada troca de quadro.
  const storeColaboracao = useMemo(() => criarStoreColaboracao(), [espaco.quadroUuid]);
  const storeNotas = useMemo(() => criarStoreNotas(apiNotas, { quadroUuid: espaco.quadroUuid }), [espaco.quadroUuid]);
  const storeEnvio = useMemo(
    () =>
      criarStoreEnvio({
        importar: (arquivos, processoUuid) => api.importar(espaco.quadroUuid, arquivos, processoUuid),
        bandeja: () => api.arquivosSemProcesso(espaco.quadroUuid),
        acao: (arquivoUuid, acao, processoUuid) => api.acaoArquivo(espaco.quadroUuid, arquivoUuid, acao, processoUuid),
      }),
    [espaco.quadroUuid],
  );
  const deps = useMemo<Dependencias>(
    () => ({
      sessao: { usuarioUuid: eu.usuario.uuid, nomeUsuario: eu.usuario.nome, empresaUuid: espaco.empresaUuid, quadroUuid: espaco.quadroUuid },
      storeQuadro,
      apiLicitacao: api,
      canal,
      storeColaboracao,
      storeNotas,
      apiNotas,
      storeNotificacoes,
      storeEnvio,
      configuracoes: criarApiConfiguracoes(api, espaco.empresaUuid),
      armazenamento,
      espacos: {
        usuario: eu.usuario,
        empresas: eu.empresas,
        atual: espaco,
        navegar,
        sair: () => void sessao.sair(),
        enviarFoto: async (arquivo: File) => {
          await api.enviarFoto(arquivo);
          await recarregar();
        },
        canais: {
          obter: () => api.obterCanais(),
          vincularTelegram: () => api.vincularTelegram(),
          desvincularTelegram: () => api.desvincularTelegram(),
        },
        convidar: (dados) => api.convidar(espaco.quadroUuid, dados),
        opcoesConvite: () => api.opcoesConvite(espaco.quadroUuid),
        conviteParaAprovar: (uuid) => api.conviteParaAprovar(espaco.quadroUuid, uuid),
        decidirConvite: (uuid, decisao, motivo) => api.decidirConvite(espaco.quadroUuid, uuid, decisao, motivo),
        membros: criarApiMembros(api, espaco.quadroUuid),
        buscar: (termo, processoUuid) => api.buscar(espaco.quadroUuid, termo, processoUuid),
        importar: (arquivos, processoUuid) => api.importar(espaco.quadroUuid, arquivos, processoUuid),
        importacoes: () => api.importacoes(espaco.quadroUuid),
      },
    }),
    [eu, espaco, navegar, recarregar, storeQuadro, storeColaboracao, storeNotas, storeEnvio],
  );
  return (
    <ProvedorDependencias valor={deps} key={espaco.quadroUuid}>
      <TelaQuadro usuario={{ nome: eu.usuario.nome, uuid: eu.usuario.uuid }} />
    </ProvedorDependencias>
  );
}

/** Páginas abertas sem sessão: o link do convite e o pedido de acesso (Google sem convite). */
function paginaPublica(caminho: string) {
  const convite = /^\/convite\/([\w-]+)\/?$/.exec(caminho);
  if (convite) return <TelaConvite token={convite[1]!} api={api} entrar={(email, senha) => sessao.entrar(email, senha)} />;
  if (caminho === '/acesso/solicitar') return <TelaSolicitarAcesso token={new URLSearchParams(window.location.search).get('t')} api={api} />;
  return null;
}

export function App() {
  return paginaPublica(window.location.pathname) ?? <AppConectado />;
}

function AppConectado() {
  const { estado, navegar, recarregar } = useSessao();

  if (estado.fase === 'carregando') return <TelaAviso titulo="Carregando…" />;
  if (estado.fase === 'falha') {
    const tentar = { rotulo: 'Tentar de novo', executar: () => void recarregar() };
    return <TelaAviso titulo="Não foi possível carregar" texto="Verifique a conexão e tente de novo." acoes={[tentar]} />;
  }
  if (estado.fase === 'entrar') {
    const erro = ERROS_ENTRADA[new URLSearchParams(window.location.search).get('erro') ?? ''];
    return <TelaEntrar entrar={(email, senha) => sessao.entrar(email, senha)} erroInicial={erro} preparar={prepararRecaptcha} />;
  }

  const { eu, espaco } = estado;
  const sair = { rotulo: 'Sair', executar: () => void sessao.sair() };
  if (espaco === null) {
    return <TelaAviso titulo="Nenhum quadro disponível" texto="Você ainda não tem acesso a nenhum quadro. Peça um convite ao administrador da sua empresa." acoes={[sair]} />;
  }
  if (espaco === 'sem-acesso') {
    const primeiro = escolherEspaco(eu.empresas, null);
    const irAoPrimeiro = primeiro && primeiro !== 'sem-acesso' ? [{ rotulo: 'Ir para o meu espaço', executar: () => navegar(primeiro) }] : [];
    return <TelaAviso titulo="Quadro não encontrado" texto="Este quadro não existe ou você não tem acesso a ele." acoes={[...irAoPrimeiro, sair]} />;
  }
  return (
    <>
      <EspacoTrabalho eu={eu} espaco={espaco} navegar={navegar} recarregar={recarregar} />
      <AvisoInatividade sessao={sessao} limiteMinutos={limiteDaEmpresa(eu.empresas, espaco.empresaUuid)} aoSair={sairPorInatividade} />
    </>
  );
}
